import {
  alConflictoDeHueco,
  caracteresQueFaltan,
  MINIMO_DEL_MOTIVO,
  motivoSuficiente,
} from '@citas/frontal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorDeApi, peticion } from '../api/cliente.js';
import type { CitaCreada, RespuestaDeCitas, Servicio } from '../api/tipos.js';
import { Aviso, Campo } from '../componentes/comunes.js';
import { instanteLargo, nombreDeZona } from '../fechas.js';
import { textos } from '../textos.js';

/**
 * Paso 5: confirmar (§9).
 *
 * El nombre y el teléfono de la persona a la que hay que llamar son
 * obligatorios: el acceso es de la empresa, así que sin ese dato el agente no
 * sabría a quién marcar. El aviso de que la llamada dura diez minutos está
 * **antes** del botón de confirmar, no después.
 */
export function PantallaConfirmacion({
  servicio,
  instanteIso,
  zona,
  alReservar,
  alVolver,
  alConflicto,
}: {
  servicio: Servicio;
  instanteIso: string;
  zona: string;
  alReservar: (cita: CitaCreada, contacto: { nombre: string; telefono: string }) => void;
  alVolver: () => void;
  /** El hueco se ocupó: hay que volver a la rejilla y refrescarla. */
  alConflicto: (mensaje: string) => void;
}) {
  const clienteDeConsultas = useQueryClient();
  const [contactoNombre, setContactoNombre] = useState('');
  const [contactoTelefono, setContactoTelefono] = useState('');
  const [contactoEmails, setContactoEmails] = useState(['']);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const motivoCorto = motivo.trim().length > 0 && !motivoSuficiente(motivo);
  const correosNormalizados = contactoEmails.map((email) => email.trim());
  const hayCorreosRepetidos = correosNormalizados.some(
    (email, indice) =>
      email !== '' &&
      correosNormalizados.findIndex(
        (otro) => otro.toLocaleLowerCase() === email.toLocaleLowerCase(),
      ) !== indice,
  );

  // El trigger cuenta el mes UTC de la cita. El cupo del mes actual puede estar
  // agotado aunque la fecha elegida pertenezca a otro mes con plazas libres.
  const mesDeLaCita = new Date(instanteIso).toISOString().slice(0, 7);
  const citas = useQuery({
    queryKey: ['citas', mesDeLaCita],
    queryFn: () => peticion<RespuestaDeCitas>(`/citas?mes=${mesDeLaCita}`),
  });
  const cupo = citas.data?.cupoMensual ?? null;
  const cupoAgotado = cupo !== null && cupo.restantes === 0;

  const reservar = useMutation({
    mutationFn: () =>
      peticion<CitaCreada>('/citas', {
        metodo: 'POST',
        cuerpo: {
          servicioId: servicio.id,
          inicio: instanteIso,
          motivo: motivo.trim(),
          contactoNombre: contactoNombre.trim(),
          contactoTelefono: contactoTelefono.trim(),
          // La zona en la que el cliente está viendo la hora, para que el
          // correo no le diga otra distinta de la que acaba de leer (D-51).
          zonaHoraria: zona,
          contactoEmails: correosNormalizados,
        },
      }),
    onSuccess: (cita) => {
      setError(null);
      alReservar(cita, { nombre: contactoNombre.trim(), telefono: contactoTelefono.trim() });
    },
    onError: (fallo: unknown) => {
      // Si el hueco se ocupó mientras se rellenaba el formulario, no se enseña
      // un error genérico: se vuelve a la rejilla ya refrescada (§9). La
      // decisión y las dos reacciones son las mismas que en el panel, en una
      // sola función compartida.
      const fueConflicto = alConflictoDeHueco(fallo, {
        olvidarHueco: () => alConflicto(textos.confirmacion.conflicto),
        refrescarHuecos: () => {
          void clienteDeConsultas.invalidateQueries({ queryKey: ['huecos'] });
        },
      });
      if (fueConflicto) return;

      setError(fallo instanceof ErrorDeApi ? fallo.message : textos.errores.generico);
    },
  });

  return (
    <section className="pantalla-confirmacion" aria-labelledby="titulo-confirmacion">
      <h2 id="titulo-confirmacion">{textos.confirmacion.titulo}</h2>
      <p className="ayuda">
        {servicio.nombre} · {instanteLargo(instanteIso, zona)} · {nombreDeZona(zona)}
      </p>

      {error !== null && <Aviso tipo="error">{error}</Aviso>}

      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          reservar.mutate();
        }}
      >
        <div className="bloque-formulario">
          <CabeceraDeBloque
            icono="persona"
            titulo={textos.confirmacion.datosContacto}
            detalle={textos.confirmacion.datosContactoAyuda}
          />
          <div className="rejilla-formulario">
            <Campo
              id="contacto-nombre"
              etiqueta={textos.confirmacion.contactoNombre}
              pista={textos.confirmacion.contactoNombreAyuda}
            >
              {(props) => (
                <input
                  {...props}
                  value={contactoNombre}
                  onChange={(evento) => setContactoNombre(evento.target.value)}
                  autoComplete="name"
                  required
                  minLength={2}
                  autoFocus
                />
              )}
            </Campo>

            <Campo
              id="contacto-telefono"
              etiqueta={textos.confirmacion.contactoTelefono}
              pista={textos.confirmacion.contactoTelefonoAyuda}
            >
              {(props) => (
                <input
                  {...props}
                  value={contactoTelefono}
                  onChange={(evento) => setContactoTelefono(evento.target.value)}
                  type="tel"
                  autoComplete="tel"
                  required
                />
              )}
            </Campo>
          </div>
        </div>

        <div className="bloque-formulario">
          <CabeceraDeBloque
            icono="correo"
            titulo={textos.confirmacion.avisosCorreo}
            detalle={textos.confirmacion.avisosCorreoAyuda}
          />
          <div className="correos-aviso">
            {contactoEmails.map((email, indice) => {
              const repetido =
                email.trim() !== '' &&
                correosNormalizados.findIndex(
                  (otro) => otro.toLocaleLowerCase() === email.trim().toLocaleLowerCase(),
                ) !== indice;
              const id = `contacto-email-${indice}`;

              return (
                <div className="correo-aviso" key={id}>
                  <Campo
                    id={id}
                    etiqueta={
                      indice === 0
                        ? textos.confirmacion.contactoEmail
                        : textos.confirmacion.contactoEmailAdicional(indice)
                    }
                    pista={indice === 0 ? textos.confirmacion.contactoEmailAyuda : undefined}
                    error={repetido ? textos.confirmacion.contactoEmailRepetido : null}
                  >
                    {(props) => (
                      <input
                        {...props}
                        value={email}
                        onChange={(evento) =>
                          setContactoEmails((actuales) =>
                            actuales.map((actual, posicion) =>
                              posicion === indice ? evento.target.value : actual,
                            ),
                          )
                        }
                        type="email"
                        autoComplete={indice === 0 ? 'email' : 'off'}
                        required
                      />
                    )}
                  </Campo>
                  {indice > 0 && (
                    <button
                      type="button"
                      className="enlace quitar-correo"
                      onClick={() =>
                        setContactoEmails((actuales) =>
                          actuales.filter((_, posicion) => posicion !== indice),
                        )
                      }
                    >
                      {textos.confirmacion.quitarCorreo}
                    </button>
                  )}
                </div>
              );
            })}

            {contactoEmails.length < 5 && (
              <button
                type="button"
                className="enlace anadir-correo"
                onClick={() => setContactoEmails((actuales) => [...actuales, ''])}
              >
                {textos.confirmacion.anadirCorreo}
              </button>
            )}
          </div>
        </div>

        <div className="bloque-formulario">
          <CabeceraDeBloque
            icono="consulta"
            titulo={textos.confirmacion.detalleConsulta}
            detalle={textos.confirmacion.detalleConsultaAyuda}
          />
          <Campo
            id="motivo"
            etiqueta={textos.confirmacion.motivo}
            pista={textos.confirmacion.motivoAyuda(MINIMO_DEL_MOTIVO)}
            error={motivoCorto ? `Faltan ${caracteresQueFaltan(motivo)} caracteres.` : null}
          >
            {(props) => (
              <span className="control-con-contador">
                <textarea
                  {...props}
                  value={motivo}
                  onChange={(evento) => setMotivo(evento.target.value)}
                  required
                  minLength={MINIMO_DEL_MOTIVO}
                  maxLength={1000}
                />
                <span className="contador-caracteres" aria-hidden="true">
                  {textos.confirmacion.contadorMotivo(motivo.length, 1000)}
                </span>
              </span>
            )}
          </Campo>
        </div>

        {/* El aviso de la duración va antes de confirmar, no después (§9). */}
        <Aviso tipo="informacion">
          {textos.confirmacion.duracion(servicio.duracionMin)}{' '}
          <strong>{textos.confirmacion.preparacion}</strong>
        </Aviso>

        {cupoAgotado && cupo !== null && cupo.limite !== null && (
          <Aviso tipo="atencion">{textos.confirmacion.cupoAgotado(cupo.limite)}</Aviso>
        )}

        <div className="botonera acciones-confirmacion">
          <button
            type="submit"
            className="principal"
            disabled={reservar.isPending || motivoCorto || hayCorreosRepetidos || cupoAgotado}
          >
            {reservar.isPending ? textos.confirmacion.reservando : textos.confirmacion.reservar}
          </button>
          <button type="button" className="enlace" onClick={alVolver}>
            {textos.aplicacion.volver}
          </button>
        </div>
      </form>
    </section>
  );
}

function CabeceraDeBloque({
  icono,
  titulo,
  detalle,
}: {
  icono: 'persona' | 'correo' | 'consulta';
  titulo: string;
  detalle: string;
}) {
  return (
    <div className="cabecera-bloque-formulario">
      <span aria-hidden="true">
        <svg viewBox="0 0 24 24">
          {icono === 'persona' && (
            <>
              <circle cx="12" cy="8" r="4" />
              <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
            </>
          )}
          {icono === 'correo' && (
            <>
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m4 7 8 6 8-6" />
            </>
          )}
          {icono === 'consulta' && (
            <>
              <path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 3v-5a2 2 0 0 1-1-1.7V6a2 2 0 0 1 2-2Z" />
              <path d="M8 9h8m-8 4h5" />
            </>
          )}
        </svg>
      </span>
      <div>
        <h3>{titulo}</h3>
        <p>{detalle}</p>
      </div>
    </div>
  );
}
